import { Vec3 } from 'playcanvas';

import type { EditOp } from './edit-ops';
import type { Events } from './events';
import type { Scene } from './scene';
import { emptyGuides, readGuides, scaleGuides, type Measurement, type Point3, type SpatialGuides } from './spatial-guides-data';
import { SpatialGuidesUI } from './spatial-guides-ui';

const registerSpatialGuides = (events: Events, scene: Scene, parent: HTMLElement) => {
    let editing: Measurement | null = null;
    const modeFor = (m: Measurement) => (m.type === 'area' ? 'areaMeasure' : m.type === 'depth' ? 'depthMeasure' : 'measure');
    const pack = (point: Vec3): Point3 => [point.x, point.y, point.z];
    const operation = (target: SpatialGuidesUI, before: SpatialGuides, after: SpatialGuides): EditOp => ({
        name: 'spatialGuides',
        do: () => {
            target.setData(after);
            events.fire('spatialGuides.changed', after);
            scene.forceRender = true;
        },
        undo: () => {
            target.setData(before);
            events.fire('spatialGuides.changed', before);
            scene.forceRender = true;
        }
    });
    const ui = new SpatialGuidesUI({
        parent,
        externalControls: true,
        showReference: true,
        excludeMeasurement: id => id === editing?.id || (id === 'scale-reference' && (events.invokeOptional('distanceScale.state')?.count ?? 0) > 0),
        saveHint: 'Included when you save the project or export a standalone viewer.',
        project: (point) => {
            const world = new Vec3(point);
            const screen = new Vec3();
            scene.camera.worldToScreen(world, screen);
            const camera = scene.camera.mainCamera;
            return {
                x: screen.x * parent.clientWidth,
                y: screen.y * parent.clientHeight,
                visible: world.sub(camera.getPosition()).dot(camera.forward) > 0
            };
        },
        pick: async (x, y) => {
            const hit = await scene.camera.intersect(x, y);
            return hit ? pack(hit.position) : null;
        },
        bounds: () => {
            if (!events.invoke('scene.allSplats').length) return null;
            const { center, halfExtents } = scene.bound;
            return { center: pack(center), size: pack(halfExtents).map(v => Math.max(1e-6, v * 2)) as Point3, rotation: [0, 0, 0], visible: true };
        },
        change: data => events.fire('edit.add', operation(ui, ui.getData(), data)),
        measuring: (active) => {
            if (active) events.fire('tool.deactivate');
        }
    });
    events.function('spatialGuides', () => ui.getData());
    events.function('measurement.editing', () => editing);
    const finishEditing = () => {
        const previous = editing;
        editing = null;
        if (previous) events.fire(`${modeFor(previous)}.clear`);
        events.fire('measurement.editing', null);
        scene.forceRender = true;
    };
    events.on('measurement.cancel', () => {
        const mode = events.invokeOptional('tool.active');
        finishEditing();
        if (['measure', 'areaMeasure', 'depthMeasure', 'distanceScale'].includes(mode)) {
            events.fire(`${mode}.clear`);
            events.fire('tool.deactivate');
        }
    });
    events.on('tool.deactivated', (mode: string) => {
        if (editing && modeFor(editing) === mode) finishEditing();
    });
    events.on('measurement.edit', (id: string) => {
        const measurement = ui.getData().measurements.find(m => m.id === id && id !== 'scale-reference');
        if (!measurement) return;
        events.fire('measurement.cancel');
        if (!events.invokeOptional('selection')) {
            const splat = events.invoke('scene.allSplats').find((s: { visible: boolean }) => s.visible);
            if (!splat) {
                events.fire('measurement.error', 'Select a visible splat layer to edit measurement points.'); return;
            }
            events.fire('selection', splat);
        }
        const mode = modeFor(measurement);
        const coordinates = measurement.type === 'area' ? measurement.points : measurement.type === 'depth' ? [...measurement.plane, measurement.point] : [measurement.start, measurement.end];
        if (!events.invokeOptional(`${mode}.load`, coordinates, measurement.type === 'depth' ? measurement.direction : 'front', measurement.type === 'depth' ? measurement.root : undefined)) return;
        editing = measurement;
        events.fire(`tool.${mode}`);
        events.fire('measurement.editing', measurement);
        scene.forceRender = true;
    });
    events.on('measurement.ensureVisible', (kind: 'reference' | 'distance' | 'area' | 'depth') => {
        const before = ui.getData();
        const key = `${kind}Visible` as 'referenceVisible' | 'distanceVisible' | 'areaVisible' | 'depthVisible';
        if (before[key] !== false && (kind === 'reference' || before.measurementsVisible)) return;
        events.fire('spatialGuides.update', { ...before, [key]: true, ...(kind === 'reference' ? {} : { measurementsVisible: true }) });
    });
    events.function('docSerialize.spatialGuides', () => ui.getData());
    const reset = (data: unknown) => {
        events.fire('measurement.cancel');
        ['measure', 'areaMeasure', 'depthMeasure', 'distanceScale'].forEach(mode => events.fire(`${mode}.clear`));
        ui.reset(readGuides(data));
        events.fire('spatialGuides.changed', ui.getData());
    };
    events.function('docDeserialize.spatialGuides', reset);
    events.on('scene.clear', () => reset(emptyGuides()));
    events.on('postrender', () => ui.render());
    events.on('exportPopup.visible', (visible: boolean) => ui.setOverlayVisible(!visible));
    events.on('spatialGuides.open', () => events.fire('measurementsPanel.setVisible', true));
    events.on('measurementsPanel.visible', (visible: boolean) => ui.setEditing(visible));
    events.on('spatialGuides.update', (data: SpatialGuides) => {
        const next = readGuides(data);
        if (editing && !next.measurements.some(m => m.id === editing.id)) events.fire('measurement.cancel');
        events.fire('edit.add', operation(ui, ui.getData(), next));
    });
    events.on('spatialGuides.fitBox', () => {
        if (!events.invoke('scene.allSplats').length) return;
        const { center, halfExtents } = scene.bound;
        events.fire('spatialGuides.update', { ...ui.getData(),
            boundingBox: {
                center: pack(center), size: pack(halfExtents).map(v => Math.max(1e-6, v * 2)) as Point3, rotation: [0, 0, 0], visible: true
            } });
    });
    events.on('tool.activated', (name: string) => {
        if (name) ui.cancelMeasurement();
    });
    events.on('measurement.save', (start: Vec3, end: Vec3, name: string) => {
        if (editing && modeFor(editing) === 'measure') {
            const changed = { ...editing, start: pack(start), end: pack(end), name: name || editing.name };
            events.fire('spatialGuides.update', { ...ui.getData(), measurements: ui.getData().measurements.map(m => (m.id === editing.id ? changed : m)) });
            finishEditing();
        } else ui.addMeasurement(pack(start), pack(end), name);
        events.fire('measure.clear');
        events.fire('measurementsPanel.setVisible', true);
    });
    events.on('advancedMeasurement.save', (kind: 'area' | 'depth', points: Vec3[], name: string, direction: 'front' | 'back', root?: Vec3) => {
        const packed = points.map(pack);
        const measurement: Measurement = kind === 'area' ? {
            id: crypto.randomUUID(), name: name || `Area ${ui.getData().measurements.filter(m => m.type === 'area').length + 1}`, type: 'area', points: packed
        } : {
            id: crypto.randomUUID(),
            name: name || `Depth ${ui.getData().measurements.filter(m => m.type === 'depth').length + 1}`,
            type: 'depth',
            plane: packed.slice(0, 3) as [Point3, Point3, Point3],
            point: packed[3],
            ...(root ? { root: pack(root) } : {}),
            direction
        };
        if (editing && modeFor(editing) === `${kind}Measure`) {
            measurement.id = editing.id;
            measurement.name = name || editing.name;
            events.fire('spatialGuides.update', { ...ui.getData(), measurements: ui.getData().measurements.map(m => (m.id === measurement.id ? measurement : m)) });
            finishEditing();
        } else events.fire('spatialGuides.update', { ...ui.getData(), measurementsVisible: true, measurements: [...ui.getData().measurements, measurement] });
        events.fire(`${kind}Measure.clear`);
        events.fire('measurementsPanel.setVisible', true);
    });
    events.function('spatialGuides.scaleOp', (start: Vec3, end: Vec3, factor: number) => {
        const before = ui.getData();
        const reference = { id: 'scale-reference', name: 'Scale reference', start: pack(start), end: pack(end) };
        const withReference: SpatialGuides = {
            ...before,
            measurementsVisible: true,
            measurements: [...before.measurements.filter(m => m.id !== reference.id), reference]
        };
        return operation(ui, before, scaleGuides(withReference, pack(start.clone().add(end).mulScalar(0.5)), factor));
    });
};

export { registerSpatialGuides };
